import { useCallback, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import { api } from '@/store/api';
import { connectAdapter } from '@/connect/adapter/streamsConnectAdapter';
import { callService } from '@/services/callService';
import { transferSseService } from '@/services/transferSse';
import { isChatContact } from '@/components/amazon-connect/contactUtils';
import {
  setCustomerOnHold,
  setWarmTransferAlert,
} from '@/store/slices/callSlice';
import {
  closeWarmTransferModal,
  resetWarmTransfer,
  selectIsWarmInProgress,
  setConsultAgentJoined,
  setWarmTransferAnsweringName,
  setWarmTransferModalView,
  setWarmTransferRequestId,
  setWarmTransferSession,
  setWarmTransferStage,
} from '@/store/slices/warmTransferSlice';
import { buildWarmTransferHandoffContext } from '@/utils/warmTransferContext';
import {
  connectionIdsBeforeDial,
  waitForThirdPartyConnectionConnected,
  watchThirdPartyConnectionEnded,
} from '@/utils/transferConnectionUtils';
import type { ConnectContact, ConnectQueueEndpoint } from '@/types';
import type {
  QueueTargetOption,
  TransferTargetOption,
} from '@/hooks/useTransferTargetOptions';

type StartTarget =
  | {
      kind: 'spog_agent' | 'teams_user' | 'teams_group';
      option: TransferTargetOption;
    }
  | { kind: 'queue'; option: QueueTargetOption };

type StartWarmTransferOptions = {
  agentNote?: string;
};

export function useWarmTransfer(activeContact: ConnectContact | undefined) {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const warmState = useAppSelector(state => state.warmTransfer);
  const isWarmInProgress = useAppSelector(selectIsWarmInProgress);
  const callState = useAppSelector(state => state.call);
  const caseState = useAppSelector(state => state.case);
  const customerOnHold = useAppSelector(state => state.call.customerOnHold);
  const [startWarmTransfer] = api.useStartWarmTransferMutation();
  const [requestAgentTransfer] = api.useRequestAgentTransferMutation();
  const [cancelWarmTransferSession] = api.useCancelWarmTransferMutation();
  const [completeWarmTransferSession] =
    api.useCompleteWarmTransferSessionMutation();

  const consultPollCleanupRef = useRef<(() => void) | null>(null);
  const consultEndedWatchCleanupRef = useRef<(() => void) | null>(null);
  const consultBeforeIdsRef = useRef<Set<string> | null>(null);
  const consultDisconnectGraceUntilRef = useRef(0);
  /** True when THIS warm transfer flow put the customer on hold. */
  const customerHeldByWarmTransferRef = useRef(false);

  const clearConsultWatchers = useCallback(() => {
    consultPollCleanupRef.current?.();
    consultPollCleanupRef.current = null;
    consultEndedWatchCleanupRef.current?.();
    consultEndedWatchCleanupRef.current = null;
  }, []);

  useEffect(() => () => clearConsultWatchers(), [clearConsultWatchers]);

  /**
   * Bring the customer back after a failed or cancelled warm transfer:
   * resume the leg this flow put on hold and clear the hold state so the
   * live transcript reappears. No-op when this flow never held the customer
   * (a pre-existing manual hold stays untouched).
   */
  const resumeCustomerAfterWarmFailure = useCallback(() => {
    if (!customerHeldByWarmTransferRef.current || !activeContact) return;
    customerHeldByWarmTransferRef.current = false;
    connectAdapter.resumeConnection(activeContact, {
      success: () => {
        dispatch(setCustomerOnHold(false));
      },
      failure: () => {
        // Banner still shows On hold — the agent can resume manually.
        console.warn('[WarmTransfer] auto-resume of customer leg failed');
      },
    });
  }, [activeContact, dispatch]);

  const failWarm = useCallback(
    (message: string) => {
      clearConsultWatchers();
      console.error('[WarmTransfer]', message);

      // Destroy any zombie consult leg left from a failed transfer attempt.
      // Without this the contact may carry a stale third-party connection that
      // interferes with getActiveConnection() and subsequent hold/resume ops.
      if (activeContact) {
        connectAdapter.destroyConsultConnection(activeContact, null, {
          success: () => {},
          failure: () => {},
        });
      }

      // Bring the customer back: a failed transfer left them on hold, which
      // silences both sides and hides the live transcript.
      resumeCustomerAfterWarmFailure();

      // Cancel the backend warm transfer session so the server doesn't keep
      // a stale session that blocks future transfers for this contact.
      const contactId = warmState.initialContactId;
      if (contactId) {
        void cancelWarmTransferSession({
          initialContactId: contactId,
          reason: 'consult_ended',
        });
      }

      dispatch(setWarmTransferAlert(message));
      dispatch(setWarmTransferStage('cancelled'));
      dispatch(closeWarmTransferModal());
      dispatch(resetWarmTransfer());
      return message;
    },
    [
      clearConsultWatchers,
      dispatch,
      activeContact,
      warmState.initialContactId,
      cancelWarmTransferSession,
      resumeCustomerAfterWarmFailure,
    ]
  );

  const handleConsultAgentDisconnected = useCallback(() => {
    clearConsultWatchers();
    console.log('[WarmTransfer] consult agent disconnected');
    const wasConference = warmState.stage === 'conference';
    dispatch(
      setWarmTransferAlert(
        wasConference
          ? 'consult_agent_disconnected_conference'
          : 'consult_agent_disconnected'
      )
    );
    dispatch(setWarmTransferStage('cancelled'));
    dispatch(closeWarmTransferModal());
    dispatch(resetWarmTransfer());
    if (!wasConference) {
      dispatch(setCustomerOnHold(true));
    }
  }, [clearConsultWatchers, dispatch, warmState.stage]);

  const dialConsultEndpoint = useCallback(
    (
      contact: ConnectContact,
      endpoint: ConnectQueueEndpoint,
      onConsulting: () => void,
      options?: {
        deferUntilAgentJoined?: boolean;
        /** Fires when the routing leg dies before an agent joins (e.g. empty queue). */
        onRoutingLegEnded?: () => void;
      }
    ) => {
      const deferUntilAgentJoined = options?.deferUntilAgentJoined ?? false;
      const beforeIds = connectionIdsBeforeDial(contact);
      consultBeforeIdsRef.current = beforeIds;
      dispatch(setWarmTransferStage('dialing'));
      console.log('[WarmTransfer] addConnection consult', {
        endpointARN: (endpoint as { endpointARN?: string }).endpointARN,
        endpointId: (endpoint as { endpointId?: string }).endpointId,
        type: (endpoint as { type?: string }).type,
      });
      connectAdapter.addConnection(contact, endpoint, {
        success: () => {
          console.log(
            '[WarmTransfer] addConnection success, waiting for consult leg'
          );
          consultPollCleanupRef.current = waitForThirdPartyConnectionConnected(
            contact,
            beforeIds,
            {
              onConnected: () => {
                if (deferUntilAgentJoined) {
                  console.log(
                    '[WarmTransfer] consult routing leg connected, awaiting warm_consult_connected'
                  );
                  if (options?.onRoutingLegEnded) {
                    consultEndedWatchCleanupRef.current =
                      watchThirdPartyConnectionEnded(contact, beforeIds, {
                        onEnded: options.onRoutingLegEnded,
                        legFilter: 'outbound_or_queue',
                        absentPollsBeforeEnd: 12,
                        assumeActiveAtStart: true,
                      });
                  }
                } else {
                  dispatch(setConsultAgentJoined(true));
                  dispatch(setWarmTransferStage('consulting'));
                }
                onConsulting();
              },
              onFailure: reason => {
                failWarm(reason);
              },
              failOnLegEnd: !deferUntilAgentJoined,
            }
          );
        },
        failure: () => failWarm('add_connection_failed'),
      });
    },
    [dispatch, failWarm]
  );

  useEffect(() => {
    if (!warmState.requestId || !activeContact) return;
    const unsubscribe = transferSseService.subscribe(event => {
      if (event.type !== 'transfer_response') return;
      if (event.requestId !== warmState.requestId) return;
      if (event.mode === 'cold') return;
      console.log('[WarmTransfer] transfer_response', event);
      if (
        event.decision === 'accept' &&
        event.quickConnect &&
        warmState.stage === 'waitingForAccept'
      ) {
        if (event.fromDisplayName || event.fromConnectId) {
          dispatch(
            setWarmTransferAnsweringName(
              event.fromDisplayName ?? event.fromConnectId
            )
          );
        }
        const endpointToUse = {
          endpointARN: event.quickConnect,
          endpointId: event.quickConnect,
          name: event.quickConnect,
          phoneNumber: null,
          queue: null,
          type: 'queue',
        } as ConnectQueueEndpoint;
        dialConsultEndpoint(activeContact, endpointToUse, () => {}, {
          deferUntilAgentJoined: true,
        });
      } else if (event.decision !== 'accept') {
        failWarm('transfer_declined');
      }
    });
    return unsubscribe;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    warmState.requestId,
    warmState.stage,
    activeContact,
    dialConsultEndpoint,
    failWarm,
  ]);

  useEffect(() => {
    if (!warmState.initialContactId) return;
    const unsubscribe = transferSseService.subscribe(event => {
      if (event.type !== 'warm_consult_connected') return;
      if (event.initialContactId !== warmState.initialContactId) {
        console.log('[WarmTransfer] warm_consult_connected ignored', {
          eventInitialContactId: event.initialContactId,
          stateInitialContactId: warmState.initialContactId,
        });
        return;
      }
      console.log('[WarmTransfer] warm_consult_connected matched', event);
      consultPollCleanupRef.current?.();
      consultPollCleanupRef.current = null;
      // Stop the routing-leg watch (empty-queue detection) — agent 2 joined.
      consultEndedWatchCleanupRef.current?.();
      consultEndedWatchCleanupRef.current = null;
      consultDisconnectGraceUntilRef.current = Date.now() + 15_000;
      dispatch(
        setWarmTransferAnsweringName(
          event.answeringDisplayName ??
            event.answeringConnectId ??
            t('components.contactCenter.warmTransferUnknownAgent', 'Agent')
        )
      );
      dispatch(setConsultAgentJoined(true));
      console.log(
        '[WarmTransfer] switching stage -> consulting from warm_consult_connected'
      );
      dispatch(setWarmTransferStage('consulting'));
    });
    return unsubscribe;
  }, [dispatch, t, warmState.initialContactId, warmState.stage]);

  useEffect(() => {
    if (!activeContact) return;
    if (!warmState.consultAgentJoined) return;
    if (warmState.stage !== 'consulting' && warmState.stage !== 'conference') {
      return;
    }
    const beforeIds = consultBeforeIdsRef.current;
    if (!beforeIds) return;

    consultEndedWatchCleanupRef.current = watchThirdPartyConnectionEnded(
      activeContact,
      beforeIds,
      {
        onEnded: handleConsultAgentDisconnected,
        ignoreUntilMs: consultDisconnectGraceUntilRef.current,
        absentPollsBeforeEnd: 10,
      }
    );

    return () => {
      consultEndedWatchCleanupRef.current?.();
      consultEndedWatchCleanupRef.current = null;
    };
  }, [
    activeContact,
    warmState.consultAgentJoined,
    warmState.stage,
    handleConsultAgentDisconnected,
  ]);

  const startWarmTransferFlow = useCallback(
    async (target: StartTarget, options?: StartWarmTransferOptions) => {
      if (!activeContact || isChatContact(activeContact)) {
        return failWarm('voice_only');
      }

      const contactId = connectAdapter.getCallScopeContactId(activeContact);
      const context = buildWarmTransferHandoffContext(
        { call: callState, case: caseState },
        {
          agentNote: options?.agentNote,
        }
      );

      const targetType = target.kind;
      const targetId = target.option.value;
      const targetDisplayName =
        target.kind === 'queue'
          ? target.option.label
          : target.option.displayName;
      const targetPhoneNumber = ['teams_user', 'teams_group'].includes(
        target.kind
      )
        ? (target.option as TransferTargetOption).targetPhoneNumber
        : undefined;

      dispatch(
        setWarmTransferSession({
          initialContactId: contactId,
          targetType,
          targetId,
          targetDisplayName,
          targetPhoneNumber: targetPhoneNumber ?? null,
        })
      );
      dispatch(setWarmTransferModalView('inProgress'));
      dispatch(setWarmTransferStage('holdingCustomer'));

      try {
        await startWarmTransfer({
          contactId,
          targetType,
          targetId,
          targetDisplayName,
          targetPhoneNumber,
          context,
        }).unwrap();
      } catch {
        return failWarm('warm_start_failed');
      }

      if (!customerOnHold) {
        try {
          await new Promise<void>((resolve, reject) => {
            connectAdapter.holdConnection(activeContact, {
              success: () => {
                customerHeldByWarmTransferRef.current = true;
                dispatch(setCustomerOnHold(true));
                resolve();
              },
              failure: err => reject(err),
            });
          });
        } catch {
          failWarm('hold_failed');
          return;
        }
      }

      if (['teams_user', 'teams_group'].includes(target.kind)) {
        const phone = (target.option as TransferTargetOption).targetPhoneNumber;
        if (!phone) return failWarm('no_phone');
        const endpoint = connectAdapter.getCcpEnums().byPhoneNumber(phone);
        dialConsultEndpoint(
          activeContact,
          endpoint as ConnectQueueEndpoint,
          () => {}
        );
        return;
      }

      if (target.kind === 'queue') {
        dialConsultEndpoint(
          activeContact,
          target.option.endpoint,
          () => {
            dispatch(setWarmTransferAnsweringName('Queue agent'));
          },
          {
            deferUntilAgentJoined: true,
            onRoutingLegEnded: () => failWarm('queue_no_answer'),
          }
        );
        return;
      }

      dispatch(setWarmTransferStage('waitingForAccept'));
      try {
        const result = await requestAgentTransfer({
          toConnectId: target.option.value,
          contactId,
          mode: 'warm',
          targetType: 'spog_agent',
          context,
        }).unwrap();
        console.log('[WarmTransfer] SPOG warm request created', {
          requestId: result.requestId,
          toConnectId: target.option.value,
        });
        dispatch(setWarmTransferRequestId(result.requestId));
      } catch {
        return failWarm('request_failed');
      }
    },
    [
      activeContact,
      dispatch,
      dialConsultEndpoint,
      failWarm,
      requestAgentTransfer,
      startWarmTransfer,
      customerOnHold,
      callState,
      caseState,
    ]
  );

  const cancelWarmTransfer = useCallback(() => {
    if (!activeContact) return;
    clearConsultWatchers();
    dispatch(setWarmTransferStage('cancelling'));
    const contactId = warmState.initialContactId;
    connectAdapter.destroyConsultConnection(activeContact, null, {
      success: () => {
        if (contactId) {
          void cancelWarmTransferSession({
            initialContactId: contactId,
            reason: 'cancelled_by_agent1',
          });
        }
        dispatch(setWarmTransferStage('cancelled'));
        dispatch(closeWarmTransferModal());
        dispatch(resetWarmTransfer());
        // The transfer is cancelled — bring the customer back off hold.
        resumeCustomerAfterWarmFailure();
      },
      failure: () => failWarm('cancel_failed'),
    });
  }, [
    activeContact,
    clearConsultWatchers,
    cancelWarmTransferSession,
    dispatch,
    failWarm,
    resumeCustomerAfterWarmFailure,
    warmState.initialContactId,
  ]);

  const conferenceWarmTransfer = useCallback(() => {
    if (!activeContact) return;
    connectAdapter.resumeConnection(activeContact, {
      success: () => {
        customerHeldByWarmTransferRef.current = false;
        dispatch(setWarmTransferStage('conference'));
        dispatch(setCustomerOnHold(false));
      },
      failure: () => failWarm('conference_failed'),
    });
  }, [activeContact, dispatch, failWarm]);

  const completeWarmTransfer = useCallback(() => {
    if (!activeContact) return;
    const needsConsultRegisteredOnBackend =
      warmState.targetType === 'queue' || warmState.targetType === 'spog_agent';
    if (needsConsultRegisteredOnBackend && !warmState.consultAgentJoined) {
      console.warn(
        '[WarmTransfer] complete blocked until warm_consult_connected',
        {
          targetType: warmState.targetType,
          stage: warmState.stage,
        }
      );
      return;
    }
    dispatch(setWarmTransferStage('completing'));
    const contactId = warmState.initialContactId;
    connectAdapter.destroyAgentConnection(activeContact, {
      success: () => {
        if (contactId) {
          void completeWarmTransferSession({
            initialContactId: contactId,
            promoteConsultToPrimary: true,
            reason: 'completed_by_agent1',
          });
        }
        customerHeldByWarmTransferRef.current = false;
        callService.clearCall();
        dispatch(setWarmTransferStage('completed'));
        dispatch(closeWarmTransferModal());
        dispatch(resetWarmTransfer());
      },
      failure: () => failWarm('complete_failed'),
    });
  }, [
    activeContact,
    completeWarmTransferSession,
    dispatch,
    failWarm,
    warmState.consultAgentJoined,
    warmState.initialContactId,
    warmState.stage,
    warmState.targetType,
  ]);

  return {
    warmState,
    isWarmInProgress,
    startWarmTransferFlow,
    cancelWarmTransfer,
    conferenceWarmTransfer,
    completeWarmTransfer,
    t,
  };
}
